import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FocusTrap from 'focus-trap-react';
import CancelIcon from '../assets/icons/CancelIcon';
import {
  DigitalLeadsWrapper,
  DigitalLeadsModal,
  CloseModal,
  Content,
  Header,
  InputSection,
  VehicleDetailSection,
  Title,
  Inputs,
  InputField,
  BlackBackground,
  Sticky,
  SectionWrapper,
} from './DitigalLeads.styles';
import VehicleInfo from './VehicleInfo/VehicleInfo';
import Submit from './Submit';
import useDealerInfo from '../hooks/useDealerInfo';
import useVehicleInfo from '../hooks/useVehicleInfo';
import useLeadsBuilder from '../hooks/useLeadsBuilder';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import LegalDisclaimer from './LegalDisclaimer';
import ResultModal from './ResultModal';
import { selectLocalConfig, setModalIsClose } from '../redux/localConfigReducer';
import { selectForm, setForm } from '../redux/formReducer';
import {
  removeVehicleInfo,
  selectDealerAndVehicleData,
  setVehicle,
} from '../redux/dealerAndVehicleReducer';
import useSourceOfLead from '../hooks/useSourceOfLead';
import { digitalLeadsFormLoad } from '../utils/analyticsEventListeners';
import {
  setUpdatedVTPLeadForm,
  setUpdatedPromotionsLeadForm,
  useGenericFormLoad,
  useSuccessfulFormSubmission,
  useUnsuccessfulFormError,
} from '../hooks/useIxisEvents';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import { FEATURE_APP_DETAILS, CAMPAIGN_ID } from '../constants';
import { Vehicle, VehicleDataType } from '../types';
import { setRemoveOffers, selectAppConfig, setAppConfig } from '../redux/appConfigReducer';
import useFetchByCarline from '../hooks/fetchHooks/useFetchByCarline';

const DigitalLeads: React.FC = () => {
  const { isModalOpen, leadId, error, errorMessage } = useSelector(selectLocalConfig);
  const [vehicleStatus, setVehicleStatus] = useState<string | undefined>('');
  const [vehicleSaleType, setVehicleSaleType] = useState<string | undefined>('');
  const [vehicleSaleOrderType, setVehicleSaleOrderType] = useState<string | undefined | null>(null);
  const [orderStatusVehicle, setOrderStatusVehicle] = useState<string | undefined | null>(null);
  const [vehicleWithEditedNames, setVehicleWithEditedNames] = useState<
    VehicleDataType | undefined
  >();
  const [vehicleDataForPromotions, setVehicleDataForPromotions] = useState<Vehicle | undefined>();
  const [prevVehicle, setPrevVehicle] = useState<VehicleDataType>();
  const dispatch = useDispatch();
  const contactForm = useSelector(selectForm);
  const { t } = useTranslation();

  const { offers, dealerId, market } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);

  const handleCloseModal = () => {
    dispatch(setRemoveOffers());
    dispatch(removeVehicleInfo());
    dispatch(setModalIsClose());
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
  };

  // const renderDigitalLeads = useMemo(() => {
  //   // First condition - if we have destop mode on (from redux)
  //   // Second condition - if no destop mode, and nobody clicked to open modal
  //   // defailt return - null in all other cases
  // }, []);

  const digitalLeadsRef = useRef(null);
  const { dealer, isLoadingDealer } = useDealerInfo();
  const { isVehicleLoading } = useVehicleInfo();

  const { vehicle, vehicleByCarline, vehicleBuAudiCode } = useSelector(selectDealerAndVehicleData);

  const vehicleInfo = vehicle || vehicleByCarline || vehicleBuAudiCode;

  const vehicleInView = vehicleInfo ?? prevVehicle;
  const isAudiMake = vehicle?.modelMake === 'Audi';

  const { topicOfInterestInput, tradeInVehicleInputs, modelSelections, buildedForm } =
    useLeadsBuilder();
  const { generalSource, specificSource: leadSource } = useSourceOfLead();

  const isUSMarket = market?.toLowerCase().includes('us');

  const isGeneralForm = !!topicOfInterestInput;
  let formName = 'VTP Lead';

  if (isGeneralForm) {
    formName = 'Generic Lead';
  } else if (leadSource === 'special_offers' || leadSource === 'offers') {
    formName = 'Promotions Lead';
  }

  const excludeYearFromModelName = useCallback(
    (currentVehicleIsAudi: boolean) => {
      let name: string | undefined;
      if (vehicleInView) {
        name = currentVehicleIsAudi
          ? vehicleInView?.modelName!.toString().slice(5)
          : vehicleInView?.modelName;
      }
      return name;
    },
    [vehicleInView],
  );

  // useEffect for data attributes
  useEffect(() => {
    const status: string | undefined =
      vehicleInView?.vehicleType === 'new' && vehicleInView?.isAvailableNow
        ? 'available'
        : vehicleInView?.vehicleOrderStatus || 'Unavailable';

    setVehicleStatus(status);

    const saleType: string | undefined =
      vehicleInView?.vehicleType === 'new' && vehicleInView?.isAvailableNow
        ? 'available-now'
        : vehicleInView?.saleOrderType || vehicleInView?.vehicleInventoryType;

    setVehicleSaleType(saleType);
  }, [vehicleInView]);

  // useEffect for Ixis form_load for VTP Lead
  useEffect(() => {
    let orderType: string | undefined | null = null;
    let orderStatus: string | undefined | null = null;

    if (vehicleInView && vehicleInView?.vehicleInventoryType !== 'available-now') {
      const { saleOrderType, vehicleOrderStatus } = vehicleInView;

      orderType = saleOrderType;
      orderStatus = vehicleOrderStatus;

      // these are for fall-back data attributes
      setVehicleSaleOrderType(saleOrderType);
      setOrderStatusVehicle(vehicleOrderStatus);
    }

    if (vehicle && isModalOpen) {
      // save vistied vehicle to state to prevent form_load trigger for same vehicle
      setPrevVehicle(vehicle);

      const vehicleTrimmedModelNames = {
        ...vehicle,
        ...{
          modelName: excludeYearFromModelName(isAudiMake),
          saleOrderType: orderType,
          vehicleOrderStatus: orderStatus,
        },
      } as VehicleDataType;

      setVehicleWithEditedNames(vehicleTrimmedModelNames);
      // set form_load event for VTP
      const updatedForm = setUpdatedVTPLeadForm(contactForm, vehicleTrimmedModelNames!);
      dispatch(setForm(updatedForm));
      digitalLeadsFormLoad(updatedForm, vehicleTrimmedModelNames, FEATURE_APP_DETAILS);
    }

    // contactForm causes infinate recursion
    // eslint-disable-next-line
  }, [dispatch, excludeYearFromModelName, isAudiMake, vehicle, isModalOpen, leadSource]);

  useEffect(() => {
    if (offers) {
      const { modelName, modelYear, trimName } = offers;

      const vehicleData = {
        modelName,
        modelYear,
        trimName,
      } as Vehicle;

      setVehicleDataForPromotions(vehicleData);

      const updatedForm = setUpdatedPromotionsLeadForm(contactForm, dealerId!, modelName);
      dispatch(setForm(updatedForm));
      digitalLeadsFormLoad(updatedForm, vehicleData, FEATURE_APP_DETAILS, promotion);
    }
    // contactForm & promotion cause infinate recursion
    // eslint-disable-next-line
  }, [offers, dealerId, dispatch]);

  useLockBodyScroll(isModalOpen);

  // Run generic form load event for Ixis
  useGenericFormLoad({ isModalOpen, leadSource, vehicle: undefined });

  // Run form submission is successful event for Ixis
  useSuccessfulFormSubmission({
    contactForm,
    leadId,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  // Run when form submission errors event for Ixis
  useUnsuccessfulFormError({
    error,
    errorMessage,
    contactForm,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  return (
    <>
      {isModalOpen && (
        <DigitalLeadsWrapper>
          <FocusTrap
            active={isModalOpen}
            focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: true }}
          >
            {!leadId && !error ? (
              <DigitalLeadsModal
                ref={digitalLeadsRef}
                data-digital-leads-form-form-name={formName}
                data-digital-leads-form-form-type="lead"
                data-digital-leads-form-form-data-model-of-interest-model-family={
                  vehicleInView?.modelName || 'null'
                }
                data-digital-leads-form-form-data-preferred-dealer={
                  vehicleInView?.dealerId || 'null'
                }
                data-digital-leads-vehicle-audi-code={vehicleInView?.audiCode || 'null'}
                data-digital-leads-vehicle-dealer-code={vehicleInView?.dealerId || 'null'}
                data-digital-leads-vehicle-dealer-name={vehicleInView?.dealerName || 'null'}
                data-digital-leads-vehicle-fuel-type={vehicleInView?.fuelType || 'null'}
                data-digital-leads-vehicle-inventory-type={
                  vehicleInView?.vehicleInventoryType || 'null'
                }
                data-digital-leads-vehicle-model-code={vehicleInView?.modelCode || 'null'}
                data-digital-leads-vehicle-model-name={
                  excludeYearFromModelName(isAudiMake) || 'null'
                }
                data-digital-leads-vehicle-model-year={vehicleInView?.modelYear || 'null'}
                data-digital-leads-vehicle-price={vehicleInView?.modelPrice || 'null'}
                data-digital-leads-vehicle-price-dealer={vehicleInView?.modelPriceRetail || 'null'}
                data-digital-leads-vehicle-sale-type={vehicleSaleType || 'null'}
                data-digital-leads-vehicle-sale-order-type={vehicleSaleOrderType || 'null'}
                data-digital-leads-vehicle-order-status={orderStatusVehicle || 'null'}
                data-digital-leads-vehicle-status={vehicleStatus || 'null'}
                data-digital-leads-vehicle-stock-number={vehicleInView?.stockNumber || 'null'}
                data-digital-leads-vehicle-vin={vehicleInView?.vin || 'null'}
                data-digital-leads-feature-app-base-url={FEATURE_APP_DETAILS.baseUrl}
                data-digital-leads-feature-app-id={FEATURE_APP_DETAILS.id}
                data-digital-leads-feature-app-name={FEATURE_APP_DETAILS.name}
              >
                <Sticky>
                  <CloseModal onClick={handleCloseModal} aria-label="close">
                    <CancelIcon />
                  </CloseModal>
                </Sticky>
                <Header>
                  {dealer?.name
                    ? t('digitalLeads.contactDealer', { dealerName: dealer.name })
                    : t('digitalLeads.contactDealerInYourArea')}
                </Header>
                <Content>
                  <InputSection>
                    <Inputs>
                      {topicOfInterestInput &&
                        topicOfInterestInput.map((topic) => {
                          return <InputField key={topic.inputKey}>{topic.Component}</InputField>;
                        })}
                      {tradeInVehicleInputs && (
                        <SectionWrapper>
                          <Title>{t('digitalLeads.inputs.vehicleDetails')}</Title>
                          {tradeInVehicleInputs.map((input) => (
                            <InputField key={input.inputKey}>{input.Component}</InputField>
                          ))}
                        </SectionWrapper>
                      )}
                      {modelSelections && (
                        <SectionWrapper>
                          <Title>{t('digitalLeads.inputs.modelSelection')}</Title>
                          {modelSelections.map((model) => (
                            <InputField key={model.inputKey}>{model.Component}</InputField>
                          ))}
                        </SectionWrapper>
                      )}

                      <Title>{t('digitalLeads.contactInfoLabel')}</Title>
                      {buildedForm.map((input) => {
                        return <InputField key={input.inputKey}>{input.Component}</InputField>;
                      })}
                    </Inputs>
                    <LegalDisclaimer />
                    <Submit />
                  </InputSection>
                  <VehicleDetailSection>
                    <VehicleInfo
                      dealer={dealer}
                      vehicle={vehicleInView}
                      isGeneralForm={isGeneralForm}
                      isLoadingDealer={isLoadingDealer}
                      isVehicleLoading={isVehicleLoading}
                    />
                  </VehicleDetailSection>
                </Content>
              </DigitalLeadsModal>
            ) : (
              <ResultModal handleCloseModal={handleCloseModal} isSuccess={!!leadId} />
            )}
          </FocusTrap>
          <BlackBackground onClick={handleCloseModal} />
        </DigitalLeadsWrapper>
      )}
    </>
  );
};

export default DigitalLeads;
